import React, { useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { Breadcrumb, Icon } from "../../ui";
import { cn } from "@/lib/utils";
import {
  metaAdsGradientCardEdgeClass,
  metaAdsLinkClass,
  metaAdsOutlineCtaClass,
  metaAdsPrimaryButtonClass,
} from "./metaAdsTheme";
import { getMetaAdsOAuthStartUrl } from "../../../hooks/metaAds/useMetaAdsApi";
import {
  broadcastMetaAdsConnectionChanged,
  META_ADS_CONNECTION_STATUS_QUERY_KEY,
  useMetaAdsConnectionStatus,
} from "../../../hooks/metaAds/useMetaAdsConnectionStatus";
import { META_ADS_CONNECT_PAGE_HIGHLIGHT_CARDS } from "./metaAdsUtils";

const MetaAdsConnectPage = () => {
  const { t } = useTranslation("metaAds");
  const { t: tNav } = useTranslation("navigation");
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const oauthUrl = getMetaAdsOAuthStartUrl();
  const { isConnected, businessName } = useMetaAdsConnectionStatus();

  const metaOauthParam = searchParams.get("meta_oauth");
  useEffect(() => {
    if (!metaOauthParam) {
      return;
    }
    if (metaOauthParam === "success") {
      queryClient.setQueryData(META_ADS_CONNECTION_STATUS_QUERY_KEY, {
        connected: true,
        adAccountId: null,
      });
      queryClient.invalidateQueries({
        queryKey: META_ADS_CONNECTION_STATUS_QUERY_KEY,
      });
      broadcastMetaAdsConnectionChanged();
      const next = new URLSearchParams(searchParams);
      next.delete("meta_oauth");
      next.delete("storeId");
      setSearchParams(next, { replace: true });
      return;
    }
    if (metaOauthParam === "error") {
      const reason = searchParams.get("reason") || "error";
      showToast(t("connect.oauthCallbackError", { reason }), "error");
      const next = new URLSearchParams(searchParams);
      next.delete("meta_oauth");
      next.delete("storeId");
      next.delete("reason");
      next.delete("msg");
      next.delete("pg");
      setSearchParams(next, { replace: true });
    }
  }, [
    metaOauthParam,
    queryClient,
    searchParams,
    setSearchParams,
    showToast,
    t,
  ]);

  const displayName = businessName || t("connect.connectedNameFallback");

  const breadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.metaAds"),
        path: "/growth/meta-ads",
        icon: "Share2",
      },
      {
        label: t("connect.breadcrumb"),
        active: true,
        disabled: true,
      },
    ],
    [t, tNav],
  );

  const handleContinueMeta = () => {
    if (oauthUrl) {
      window.location.href = oauthUrl;
      return;
    }
    showToast(t("connect.oauthMissing"), "error");
  };

  const handleContinueToCampaign = () => {
    navigate("/growth/meta-ads/create");
  };

  return (
    <div
      className={cn(
        "flex min-h-[calc(100dvh-5.5rem)] flex-col",
        "sm:min-h-[calc(100dvh-6rem)]",
      )}
    >
      <div className="shrink-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex flex-1 flex-col justify-center py-6 sm:py-8">
        <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
          <div className={metaAdsGradientCardEdgeClass}>
            <div
              className={cn(
                "bg-card text-card-foreground relative overflow-hidden rounded-2xl p-6 sm:p-8",
              )}
            >
              {!isConnected ? (
                <div className="relative z-[1] flex flex-col items-center text-center">
                  <div
                    className={cn(
                      "mb-4 flex h-12 w-12 items-center justify-center rounded-xl",
                      "border-border bg-primary/10 text-brand-cyan border",
                    )}
                  >
                    <Icon name="Share2" className="h-6 w-6" aria-hidden />
                  </div>
                  <h1 className="text-xl font-bold sm:text-2xl">
                    {t("connect.title")}
                  </h1>
                  <p className="text-muted-foreground mt-3 text-sm leading-relaxed sm:text-base">
                    {t("connect.body")}
                  </p>
                  <button
                    type="button"
                    onClick={handleContinueMeta}
                    className={cn(
                      "mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold",
                      metaAdsOutlineCtaClass,
                      "hover:text-foreground/90",
                    )}
                  >
                    <Icon name="Link2" className="h-5 w-5" aria-hidden />
                    {t("connect.ctaMeta")}
                  </button>
                  <p className="text-muted-foreground mt-4 text-xs leading-relaxed">
                    {t("connect.finePrint")}
                  </p>
                  <Link
                    to="/growth/meta-ads"
                    className={cn(
                      "mt-5 inline-flex items-center justify-center text-sm font-medium",
                      metaAdsLinkClass,
                    )}
                  >
                    {t("connect.backToHub")}
                  </Link>
                </div>
              ) : (
                <div className="relative z-[1] flex flex-col items-center text-center">
                  <div
                    className={cn(
                      "mb-4 flex h-12 w-12 items-center justify-center rounded-xl",
                      "border-success/40 bg-success/10 text-success border",
                    )}
                  >
                    <Icon
                      name="Check"
                      className="h-6 w-6"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  </div>
                  <h1 className="text-xl font-bold sm:text-2xl">
                    {t("connect.connectedHeadline")}
                  </h1>
                  <p className="text-foreground/95 mt-2 text-sm font-medium">
                    {t("connect.connectedAs", { name: displayName })}
                  </p>
                  <p className="text-muted-foreground mt-3 text-sm leading-relaxed sm:text-base">
                    {t("connect.connectedSubtext")}
                  </p>
                  <button
                    type="button"
                    onClick={handleContinueToCampaign}
                    className={cn(
                      "mt-8 flex h-12 w-full items-center justify-center gap-2 rounded-xl px-4 text-base font-semibold",
                      metaAdsPrimaryButtonClass,
                    )}
                  >
                    {t("connect.continueCreate")}
                    <Icon name="ArrowRight" className="h-5 w-5" aria-hidden />
                  </button>
                  <Link
                    to="/growth/meta-ads/performance"
                    className={cn("mt-5 text-sm font-medium", metaAdsLinkClass)}
                  >
                    {t("connect.viewPerformance")}
                  </Link>
                </div>
              )}
            </div>
          </div>

          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-3" role="list">
            {META_ADS_CONNECT_PAGE_HIGHLIGHT_CARDS.map((row) => (
              <li
                key={row.titleKey}
                className="border-border bg-card flex flex-col rounded-lg border p-4 text-center"
              >
                <Icon
                  name={row.icon}
                  className="text-brand-cyan mx-auto h-5 w-5"
                  aria-hidden
                />
                <p className="text-foreground mt-3 text-sm font-semibold">
                  {t(row.titleKey)}
                </p>
                <p className="text-muted-foreground mt-1.5 text-xs leading-relaxed">
                  {t(row.bodyKey)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MetaAdsConnectPage;
